import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Chip,
  Fade,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import HeadsetMicRoundedIcon from '@mui/icons-material/HeadsetMicRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeModeContext';

// Zelorich Brand Custom Emblem
/** Purpose: Resolve zelorich logo in the login module. Why: This keeps the surrounding interface operation aligned with the expected data shape. */
const ZelorichLogo = ({ size = 44 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: `${size * 0.28}px`,
      background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 50%, #0284C7 100%)',
      p: '2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(37, 99, 235, 0.45)',
      flexShrink: 0,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: `${size * 0.26}px`,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <img
        src="/favicon-transparent-512x512.png"
        alt="Zelorich Logo"
        style={{
          width: '88%',
          height: '88%',
          objectFit: 'contain',
        }}
      />
    </Box>
  </Box>
);

/** Purpose: collect credentials and complete sign-in. Why: authentication needs a dedicated entry point that can route users into authorized workspaces. */
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { login, token, user, loading: authLoading } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();

  const isDark = mode === 'dark';

  useEffect(() => {
    // If already logged in, redirect to role-aware home
    if (token && !authLoading) {
      navigate(
        user?.isSuperAdmin === true
          ? '/admin'
          : user?.role === 'AGENT'
            ? '/inbox'
            : '/analytics',
        { replace: true }
      );
    }

    // Show success message if passed in location state
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [token, authLoading, user, navigate, location]);

  /** Purpose: Handle handle submit in the login module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const result = await login(email, password);

      navigate(
        result?.user?.isSuperAdmin === true
          ? '/admin'
          : result?.user?.role === 'AGENT'
            ? '/inbox'
            : '/analytics'
      );
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Authentication failed. Please check your credentials and try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const featurePills = [
    {
      icon: <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18, color: '#38BDF8' }} />,
      label: 'Web & WhatsApp Chatbots',
    },
    {
      icon: <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: '#34D399' }} />,
      label: 'Voice IVR Telephony',
    },
    {
      icon: <HeadsetMicRoundedIcon sx={{ fontSize: 18, color: '#F472B6' }} />,
      label: 'Live Agent Desk & Handoff',
    },
    {
      icon: <HubRoundedIcon sx={{ fontSize: 18, color: '#A78BFA' }} />,
      label: 'Visual Studio & Flow Engine',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        minHeight: '100svh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        overflow: 'hidden',
        background: isDark
          ? 'linear-gradient(135deg, #070B14 0%, #0B1220 54%, #101827 100%)'
          : 'linear-gradient(135deg, #F4F7FB 0%, #FFFFFF 52%, #F3F6FC 100%)',
        color: isDark ? '#F8FAFC' : '#0F172A',
      }}
    >
      {/* Dynamic Ambient Background Lights */}
      <Box
        sx={{
          position: 'absolute',
          top: -160,
          left: -140,
          width: 520,
          height: 520,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(37,99,235,0.28) 0%, rgba(2,132,199,0.12) 45%, transparent 70%)'
            : 'radial-gradient(circle, rgba(37,99,235,0.14) 0%, rgba(147,197,253,0.1) 50%, transparent 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -180,
          right: -120,
          width: 600,
          height: 600,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, rgba(37,99,235,0.08) 50%, transparent 70%)'
            : 'radial-gradient(circle, rgba(167,139,250,0.12) 0%, rgba(199,210,254,0.08) 50%, transparent 70%)',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '35%',
          left: '42%',
          width: 380,
          height: 380,
          borderRadius: '50%',
          background: isDark
            ? 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%)',
          filter: 'blur(85px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Subtle Grid Pattern Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.04 : 0.025,
          backgroundImage:
            'radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* ========================================================================= */}
      {/* LEFT PANEL: SaaS Ecosystem & Enterprise Showcase (Desktop/Tablet)        */}
      {/* ========================================================================= */}
      <Box
        sx={{
          flex: { md: '0 0 46%', lg: '0 0 46%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1,
          p: { md: 4, lg: 5, xl: 6 },
          borderRight: '1px solid rgba(255,255,255,0.10)',
          background: isDark
            ? 'linear-gradient(150deg, #101827 0%, #17243A 58%, #1C3150 100%)'
            : 'linear-gradient(150deg, #17243A 0%, #203653 58%, #294565 100%)',
        }}
      >
        {/* Brand Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ZelorichLogo size={46} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.15,
                    fontSize: '1.35rem',
                    color: '#F8FAFC',
                  }}
                >
                  Zelorich
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(226, 232, 240, 0.68)',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: '0.68rem',
                  display: 'block',
                  mt: 0.25,
                }}
              >
                CUSTOMER CONVERSATION PLATFORM
              </Typography>
            </Box>
          </Box>

        </Box>

        {/* Central Hero Body */}
        <Box sx={{ my: 'auto', py: { md: 2, lg: 3 }, maxWidth: 650 }}>
          {/* Tagline Badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 0.6,
                borderRadius: '9999px',
                background: 'rgba(37, 99, 235, 0.16)',
                border: '1px solid',
                borderColor: 'rgba(147, 197, 253, 0.24)',
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: '#38BDF8' }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  letterSpacing: '0.02em',
                  color: '#BFDBFE',
                }}
              >
                ONE WORKSPACE FOR EVERY CONVERSATION
              </Typography>
            </Box>
          </Box>

          {/* Main Headline */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 750,
              lineHeight: 1.12,
              letterSpacing: '-0.04em',
              mb: 2,
              fontSize: { md: '2.15rem', lg: '2.55rem', xl: '2.85rem' },
              maxWidth: 620,
              color: '#F8FAFC',
            }}
          >
            Customer conversations, clearly connected.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(226, 232, 240, 0.76)',
              lineHeight: 1.7,
              fontSize: { md: '0.98rem', lg: '1.02rem' },
              maxWidth: 560,
              mb: 2.5,
            }}
          >
            Build automated journeys, bring every channel into one inbox, and involve your team when a person is needed.
          </Typography>

          {/* Feature Badges Grid */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.85, mb: 2.25, maxWidth: 640 }}>
            {featurePills.map((feat, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 1.5,
                  py: 0.8,
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.07)',
                  border: '1px solid',
                  borderColor: 'rgba(226, 232, 240, 0.14)',
                  boxShadow: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: '#3B82F6',
                    backgroundColor: 'rgba(255, 255, 255, 0.11)',
                  },
                }}
              >
                {feat.icon}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.84rem',
                    color: '#E2E8F0',
                  }}
                >
                  {feat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Compact product journey preview */}
          <Box
            sx={{
              p: { md: 1.75, lg: 2.25 },
              borderRadius: '18px',
              backgroundColor: 'rgba(9, 16, 29, 0.34)',
              border: '1px solid rgba(203, 213, 225, 0.16)',
              maxWidth: 560,
            }}
          >
            <Typography sx={{ color: '#E2E8F0', fontWeight: 650, fontSize: '0.88rem', mb: 1.5 }}>
              A connected support journey
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flexWrap: 'wrap' }}>
              {[
                'Bot handles routine questions',
                'Agent takes over with context',
                'Team resolves the conversation',
              ].map((step, index) => (
                <Box
                  key={step}
                  sx={{
                    flex: '1 1 135px',
                    p: 1.25,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.055)',
                    border: '1px solid rgba(203, 213, 225, 0.1)',
                  }}
                >
                  <Typography sx={{ color: '#93C5FD', fontSize: '0.7rem', fontWeight: 750, mb: 0.6 }}>
                    0{index + 1}
                  </Typography>
                  <Typography sx={{ color: '#E2E8F0', fontSize: '0.78rem', lineHeight: 1.45, fontWeight: 550 }}>
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Retained for reference; replaced by the compact product preview above. */}
          <Box
            sx={{
              display: 'none',
              p: { md: 1.75, lg: 2 },
              borderRadius: '20px',
              backgroundColor: isDark ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.9)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(226, 232, 240, 0.9)',
              boxShadow: isDark
                ? '0 24px 48px -12px rgba(0, 0, 0, 0.65)'
                : '0 20px 40px -12px rgba(15, 23, 42, 0.08)',
              backdropFilter: 'blur(20px)',
              maxWidth: 580,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top Bar of Simulation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SmartToyRoundedIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.86rem', lineHeight: 1.2 }}>
                    Zelorich AI Agent
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600, fontSize: '0.72rem' }}>
                    ● Online • Connected to Live Support
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Chip size="small" label="Web" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(56,189,248,0.12)', color: '#38BDF8' }} />
                <Chip size="small" label="WhatsApp" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(34,197,94,0.12)', color: '#22C55E' }} />
                <Chip size="small" label="Voice" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(168,85,247,0.12)', color: '#A855F7' }} />
              </Box>
            </Box>

            {/* Conversation Flow Sample */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15, mb: 1.5 }}>
              {/* Visitor message */}
              <Box sx={{ alignSelf: 'flex-end', maxWidth: '82%' }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: '16px 16px 4px 16px',
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    color: '#FFFFFF',
                    fontSize: '0.84rem',
                    fontWeight: 500,
                    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
                  }}
                >
                  "Can you connect me with a live support expert for enterprise setup?"
                </Box>
              </Box>

              {/* Bot response */}
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '88%' }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: '16px 16px 16px 4px',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#E2E8F0',
                    color: isDark ? '#F1F5F9' : '#0F172A',
                    fontSize: '0.84rem',
                    lineHeight: 1.45,
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.84rem' }}>
                    Routing you to our Enterprise Team with full session context... ⚡
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      p: 0.8,
                      borderRadius: '8px',
                      bgcolor: isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.08)',
                      border: '1px solid rgba(16,185,129,0.2)',
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#10B981' }} />
                    <Typography variant="caption" sx={{ color: isDark ? '#34D399' : '#059669', fontWeight: 600 }}>
                      Live Agent Lucky accepted the conversation.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Metrics Footer inside card */}
            <Box
              sx={{
              pt: 1.25,
                borderTop: '1px solid',
          borderColor: 'rgba(226, 232, 240, 0.16)',
                display: 'flex',
                alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <SpeedRoundedIcon sx={{ fontSize: 16, color: '#38BDF8' }} />
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, fontSize: '0.74rem' }}>
                  &lt; 200ms Voice Latency
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <LanguageRoundedIcon sx={{ fontSize: 16, color: '#A78BFA' }} />
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, fontSize: '0.74rem' }}>
                  95+ Neural Languages
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <ShieldOutlinedIcon sx={{ fontSize: 16, color: '#34D399' }} />
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, fontSize: '0.74rem' }}>
                  SOC-2 / TLS Encrypted
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Left Panel Footer */}
        <Box
          sx={{
            pt: 2,
            borderTop: '1px solid',
          borderColor: 'rgba(226, 232, 240, 0.16)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 17, color: '#93C5FD' }} />
            <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.72)', fontSize: '0.76rem', fontWeight: 500 }}>
              Automation, omnichannel inbox, and agent handoff
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.48)', fontSize: '0.74rem' }}>
            Zelorich workspace
          </Typography>
        </Box>
      </Box>

      {/* ========================================================================= */}
      {/* RIGHT PANEL: SaaS Authentication Console Form                             */}
      {/* ========================================================================= */}
      <Box
        sx={{
          flex: { md: '1 1 54%', lg: '1 1 54%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: { xs: 2, sm: 3, md: 4, lg: 5 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Top Right Header Controls: Pricing & Theme Switcher */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<LocalOfferOutlinedIcon sx={{ color: '#10B981', fontSize: 17 }} />}
            onClick={() => navigate('/pricing')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              borderRadius: '10px',
              borderColor: isDark ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.5)',
              color: isDark ? '#34D399' : '#059669',
              bgcolor: isDark ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.05)',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)',
                borderColor: '#10B981',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Plans &amp; Pricing
          </Button>

          <Tooltip title={`Switch to ${isDark ? 'light' : 'dark'} mode`}>
            <IconButton
              onClick={toggleMode}
              size="small"
              sx={{
                width: 38,
                height: 38,
                color: isDark ? '#94A3B8' : '#64748B',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
                boxShadow: isDark ? 'none' : '0 2px 5px rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9',
                  color: isDark ? '#FFFFFF' : '#0F172A',
                },
              }}
            >
              {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Central Auth Container */}
        <Fade in timeout={450}>
          <Box sx={{ width: '100%', maxWidth: 440 }}>
            {/* Mobile Branding (shown only on small screens where left panel is hidden) */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3.5,
                mt: 3,
              }}
            >
              <ZelorichLogo size={52} />
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 1.5 }}>
                Zelorich AI
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.85rem' }}>
                Enterprise Conversational Platform
              </Typography>
            </Box>

            {/* Main Auth Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3.5, md: 4 },
                borderRadius: { xs: '18px', sm: '24px' },
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.85)' : '#FFFFFF',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(226, 232, 240, 0.95)',
                boxShadow: isDark
                  ? '0 20px 48px -22px rgba(0, 0, 0, 0.72)'
                  : '0 28px 72px -32px rgba(15, 23, 42, 0.32)',
                backdropFilter: 'blur(16px)',
                position: 'relative',
              }}
            >
              {/* Card Header */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    px: 1.4,
                    py: 0.45,
                    borderRadius: '8px',
                    bgcolor: isDark ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.08)',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.2)',
                    color: isDark ? '#93C5FD' : '#2563EB',
                    mb: 1.5,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 15 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.74rem', letterSpacing: '0.04em' }}>
                    WORKSPACE SIGN-IN
                  </Typography>
                </Box>

                <Typography
                  variant="h5"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    color: isDark ? '#F8FAFC' : '#0F172A',
                    fontSize: '1.65rem',
                    mb: 0.75,
                  }}
                >
                  Welcome back
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? '#94A3B8' : '#64748B',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  Enter your credentials to access your Zelorich workspace
                </Typography>
              </Box>

              {/* Status & Error Alerts */}
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2.5,
                    borderRadius: '14px',
                    fontSize: '0.86rem',
                    alignItems: 'center',
                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEF2F2',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#FCA5A5',
                    color: isDark ? '#FCA5A5' : '#991B1B',
                  }}
                >
                  {error}
                </Alert>
              )}

              {successMessage && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 2.5,
                    borderRadius: '14px',
                    fontSize: '0.86rem',
                    alignItems: 'center',
                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ECFDF5',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#6EE7B7',
                    color: isDark ? '#6EE7B7' : '#065F46',
                  }}
                >
                  {successMessage}
                </Alert>
              )}

              {/* Login Form */}
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {/* Email Field */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.8,
                      fontWeight: 600,
                      color: isDark ? '#CBD5E1' : '#334155',
                      fontSize: '0.84rem',
                    }}
                  >
                    Work Email
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutlineRoundedIcon
                            sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 21 }}
                          />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '14px',
                        backgroundColor: isDark ? '#111827' : '#F8FAFC',
                        fontSize: '0.94rem',
                        transition: 'all 0.2s ease',
                        '& input': {
                          color: isDark ? '#F8FAFC' : '#0F172A',
                          py: 1.5,
                        },
                        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                          WebkitBoxShadow: isDark
                            ? '0 0 0 1000px #111827 inset !important'
                            : '0 0 0 1000px #F8FAFC inset !important',
                          boxShadow: isDark
                            ? '0 0 0 1000px #111827 inset !important'
                            : '0 0 0 1000px #F8FAFC inset !important',
                          WebkitTextFillColor: isDark ? '#F8FAFC !important' : '#0F172A !important',
                          caretColor: isDark ? '#F8FAFC' : '#0F172A',
                          transition: 'background-color 5000s ease-in-out 0s',
                        },
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#E2E8F0',
                          borderWidth: '1.5px',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#CBD5E1',
                        },
                        '&.Mui-focused': {
                          boxShadow: isDark
                            ? '0 0 0 4px rgba(37,99,235,0.25)'
                            : '0 0 0 4px rgba(37,99,235,0.15)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563EB',
                          borderWidth: '1.5px',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box sx={{ mb: 3.2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.8,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? '#CBD5E1' : '#334155',
                        fontSize: '0.84rem',
                      }}
                    >
                      Password
                    </Typography>

                    {/* Forgot Password Link - Preserved for tests & user reset flow */}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => navigate('/reset-password', { state: { email } })}
                      sx={{
                        p: 0,
                        minWidth: 'auto',
                        fontSize: '0.8rem',
                        color: isDark ? '#60A5FA' : '#2563EB',
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                          background: 'transparent',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot password?
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon
                            sx={{ color: isDark ? '#64748B' : '#94A3B8', fontSize: 21 }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            sx={{ color: isDark ? '#64748B' : '#94A3B8' }}
                          >
                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '14px',
                        backgroundColor: isDark ? '#111827' : '#F8FAFC',
                        fontSize: '0.94rem',
                        transition: 'all 0.2s ease',
                        '& input': {
                          color: isDark ? '#F8FAFC' : '#0F172A',
                          py: 1.5,
                        },
                        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
                          WebkitBoxShadow: isDark
                            ? '0 0 0 1000px #111827 inset !important'
                            : '0 0 0 1000px #F8FAFC inset !important',
                          boxShadow: isDark
                            ? '0 0 0 1000px #111827 inset !important'
                            : '0 0 0 1000px #F8FAFC inset !important',
                          WebkitTextFillColor: isDark ? '#F8FAFC !important' : '#0F172A !important',
                          caretColor: isDark ? '#F8FAFC' : '#0F172A',
                          transition: 'background-color 5000s ease-in-out 0s',
                        },
                        '& fieldset': {
                          borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#E2E8F0',
                          borderWidth: '1.5px',
                        },
                        '&:hover fieldset': {
                          borderColor: isDark ? 'rgba(255,255,255,0.25)' : '#CBD5E1',
                        },
                        '&.Mui-focused': {
                          boxShadow: isDark
                            ? '0 0 0 4px rgba(37,99,235,0.25)'
                            : '0 0 0 4px rgba(37,99,235,0.15)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#2563EB',
                          borderWidth: '1.5px',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Submit Sign In Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  endIcon={!loading && <ArrowForwardRoundedIcon sx={{ fontSize: 19 }} />}
                  sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    fontWeight: 750,
                    fontSize: '0.96rem',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 50%, #4F46E5 100%)',
                    boxShadow: '0 8px 24px -4px rgba(37, 99, 235, 0.45)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 50%, #4338CA 100%)',
                      boxShadow: '0 12px 28px -4px rgba(37, 99, 235, 0.6)',
                      transform: 'translateY(-1.5px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: '#ffffff' }} />
                  ) : (
                    'Sign In to Dashboard'
                  )}
                </Button>

                {/* Enterprise Access Notice - Replaces any Sign Up link */}
                <Box
                  sx={{
                    mt: 3,
                    p: 1.75,
                    borderRadius: '12px',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
                    border: '1px solid',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#E2E8F0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                  }}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: 18, color: isDark ? '#60A5FA' : '#2563EB', mt: 0.1, flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDark ? '#94A3B8' : '#64748B',
                      fontSize: '0.78rem',
                      lineHeight: 1.45,
                    }}
                  >
                    <strong>Enterprise Access:</strong> Account registration is invitation-only.
                    New team members are provisioned by your workspace administrator.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Enterprise Security Watermark Footer */}
            <Box
              sx={{
                mt: 3.5,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: isDark ? '#64748B' : '#94A3B8',
                }}
              >
                <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                  Secure sign-in to your Zelorich workspace
                </Typography>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: isDark ? '#475569' : '#94A3B8',
                  fontSize: '0.74rem',
                }}
              >
                &copy; {new Date().getFullYear()} Zelorich AI. All rights reserved.
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
}
